import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  FormatListBulleted as FormatListBulletedIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';
import { imprimirListadoGrande } from 'lib/impresionhtml';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

export const PROGRAMAS_LISTADO = [
  'listadoclientessuplidores',
  'registroclientessuplidores',
  'clientessuplidores',
];

const ListadoClientesSuplidores = ({ onSeleccionarCliente }) => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;

  const standalone = typeof onSeleccionarCliente !== 'function';

  const [tipoFiltro, setTipoFiltro] = useState('Todos');
  const [filtrosColumna, setFiltrosColumna] = useState({
    nombre: '',
    rnc: '',
    ciudad: '',
    telefono: '',
    email: '',
  });
  const [clientesSuplidores, setClientesSuplidores] = useState([]);
  const [clientesSuplidoresFiltrados, setClientesSuplidoresFiltrados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoVer, setDialogoVer] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina] = useState(10);

  // 'listadoclientessuplidores' no existe en el editor de roles (solo lo
  // tienen un par de negocios sembrados a mano): la pestaña vive dentro de
  // Clientes / Suplidores, así que basta el permiso de esa pantalla o del
  // módulo, igual que exige el backend (/api/clientesbackend_buscar).
  const tienePermiso = PROGRAMAS_LISTADO.some((p) => tienePermisosModulo(roleDetalles, p));

  useEffect(() => {
    if (!tienePermiso) return;
    cargarDatos();
  }, [selectedCompany, tienePermiso]);

  useEffect(() => {
    filtrarDatos();
  }, [filtrosColumna, tipoFiltro, clientesSuplidores]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const responseClientes = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: 'Cliente',
        descripcion: '',
      });

      const responseSuplidores = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: 'Suplidor',
        descripcion: '',
      });

      let todosLosDatos = [];

      if (responseClientes.ok) {
        todosLosDatos = [...todosLosDatos, ...(responseClientes.data.resultados || [])];
      }

      if (responseSuplidores.ok) {
        todosLosDatos = [...todosLosDatos, ...(responseSuplidores.data.resultados || [])];
      }

      setClientesSuplidores(todosLosDatos);
    } catch (error) {
      showError('Error de conexión al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  const filtrarDatos = () => {
    let datosFiltrados = [...clientesSuplidores];

    if (tipoFiltro !== 'Todos') {
      datosFiltrados = datosFiltrados.filter((item) => item.tiposuplidor === tipoFiltro);
    }

    const { nombre: qNombre, rnc: qRnc, ciudad: qCiudad, telefono: qTelefono, email: qEmail } =
      filtrosColumna;

    if (qNombre.trim()) {
      const v = qNombre.toLowerCase();
      datosFiltrados = datosFiltrados.filter((item) => item.descrip?.toLowerCase().includes(v));
    }
    if (qRnc.trim()) {
      const v = qRnc.trim();
      datosFiltrados = datosFiltrados.filter((item) => item.rnc?.includes(v));
    }
    if (qCiudad.trim()) {
      const v = qCiudad.toLowerCase();
      datosFiltrados = datosFiltrados.filter((item) => item.ciudad?.toLowerCase().includes(v));
    }
    if (qTelefono.trim()) {
      const v = qTelefono.toLowerCase();
      datosFiltrados = datosFiltrados.filter(
        (item) => item.telefono != null && String(item.telefono).toLowerCase().includes(v)
      );
    }
    if (qEmail.trim()) {
      const v = qEmail.toLowerCase();
      datosFiltrados = datosFiltrados.filter((item) => item.email?.toLowerCase().includes(v));
    }

    setClientesSuplidoresFiltrados(datosFiltrados);
    setPagina(0);
  };

  // Imprime el listado filtrado con el formato compartido estilo factura x
  // mayor (lib/impresionhtml).
  const imprimirListado = () => {
    if (!clientesSuplidoresFiltrados.length) {
      showError('No hay registros para imprimir');
      return;
    }
    const etiquetas = { nombre: 'Nombre', rnc: 'RNC', ciudad: 'Ciudad', telefono: 'Teléfono', email: 'Email' };
    const filtros = [
      tipoFiltro !== 'Todos' ? `Tipo: ${tipoFiltro}` : '',
      ...Object.entries(filtrosColumna)
        .filter(([, v]) => String(v).trim())
        .map(([k, v]) => `${etiquetas[k] || k}: "${String(v).trim()}"`),
    ]
      .filter(Boolean)
      .join(' · ');
    imprimirListadoGrande({
      titulo: 'Listado de Clientes y Suplidores',
      subtitulo: filtros || 'Todos los registros',
      cabeceras: ['Tipo', 'Nombre', 'RNC/Cédula', 'Ciudad', 'Teléfono', 'Email'],
      filas: clientesSuplidoresFiltrados.map((c) => [
        c.tiposuplidor || '',
        c.descrip || '',
        c.rnc || '',
        c.ciudad || '',
        c.telefono || '',
        c.email || '',
      ]),
    });
  };

  const handleVerDetalles = async (id) => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (ok) {
        setClienteSeleccionado(data.cliente);
        setDialogoVer(true);
      } else {
        showError('Error al obtener los detalles');
      }
    } catch (error) {
      showError('Error de conexión');
    }
  };

  const handleEditar = (id) => {
    if (onSeleccionarCliente) {
      onSeleccionarCliente(id);
    }
  };

  const handleEliminar = async (id, tipo) => {
    if (!window.confirm('¿Está seguro que desea eliminar este registro?')) {
      return;
    }

    try {
      const responseGet = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (responseGet.ok) {
        const rnc = responseGet.data.cliente.rnc;

        const { ok, data } = await post('/api/clientesbackend_eliminar', {
          id_empresa: selectedCompany,
          rnc: rnc,
          tipo: tipo,
        });

        if (ok) {
          showSuccess('Registro eliminado exitosamente');
          cargarDatos();
        } else {
          showError(data.Error || 'Error al eliminar');
        }
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const limpiarFiltros = () => {
    setFiltrosColumna({
      nombre: '',
      rnc: '',
      ciudad: '',
      telefono: '',
      email: '',
    });
    setTipoFiltro('Todos');
  };

  const inicio = pagina * filasPorPagina;
  const fin = inicio + filasPorPagina;
  const datosPaginados = clientesSuplidoresFiltrados.slice(inicio, fin);
  const totalPaginas = Math.ceil(clientesSuplidoresFiltrados.length / filasPorPagina);

  const primaryBtnSx = {
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
  };

  const outlineBtnSx = {
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    bgcolor: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
  };

  if (!tienePermiso) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: standalone ? '100vh' : 360,
          bgcolor: standalone ? FP.color.surface : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso Denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permisos para acceder al listado y mantenimiento de Clientes y Suplidores.
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    );
  }

  const SectionHeader = ({ icon, label }) => (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.6,
        borderBottom: `1px solid ${FP.color.border}`,
        bgcolor: '#FAFBFE',
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.2,
          bgcolor: FP.color.accentBlueSoft,
          color: FP.color.accentBlue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
        {label}
      </Typography>
    </Box>
  );

  const FpCard = ({ children, sx: extraSx }) => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        ...extraSx,
      }}
    >
      {children}
    </Card>
  );

  const chipTipoSx = (esCliente) =>
    esCliente
      ? {
          bgcolor: FP.color.accentBlueSoft,
          color: FP.color.accentBlue,
          fontWeight: 600,
          '& .MuiChip-icon': { color: FP.color.accentBlue },
        }
      : {
          bgcolor: FP.color.infoSoft,
          color: FP.color.info,
          fontWeight: 600,
          '& .MuiChip-icon': { color: FP.color.info },
        };

  /** Título encima de cada filtro, alineado con la columna de datos del mismo nombre. */
  const filtroColumnTituloSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  /** Mismas anchos en filtros y en tabla de datos para alinear títulos y celdas. */
  const registrosAnchosColumnas = ['11%', '22%', '11%', '14%', '12%', '19%', '11%'];
  const registrosColgroup = (
    <colgroup>
      {registrosAnchosColumnas.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
  const registrosTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: { xs: 720, sm: 880 },
  };

  const registrarFiltroColumna = (campo, valor) => {
    setFiltrosColumna((prev) => ({ ...prev, [campo]: valor }));
  };

  const contenido = (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: FP.color.textPrimary }}>
          Listado de Clientes y Suplidores
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={imprimirListado} sx={outlineBtnSx}>
            Imprimir
          </Button>
          <Button variant="outlined" startIcon={<ClearIcon />} onClick={limpiarFiltros} sx={outlineBtnSx}>
            Limpiar
          </Button>
          <Button variant="contained" onClick={cargarDatos} sx={primaryBtnSx}>
            Actualizar
          </Button>
        </Box>
      </Box>

      <FpCard sx={{ mb: 3 }}>
        <SectionHeader icon={<FilterListIcon sx={{ fontSize: 18 }} />} label="Filtros de búsqueda" />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer
            sx={{
              overflowX: 'auto',
              py: 2,
              px: 0,
              scrollbarGutter: 'stable',
            }}
          >
            <Table
              size="medium"
              sx={{
                ...registrosTableSx,
                '& td': { border: 'none', verticalAlign: 'top', px: 2, py: 1.5 },
              }}
            >
              {registrosColgroup}
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      Tipo
                    </Typography>
                    <FormControl fullWidth size="medium" variant="outlined" hiddenLabel sx={fpInputSx}>
                      <Select
                        value={tipoFiltro}
                        onChange={(e) => setTipoFiltro(e.target.value)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Filtrar por tipo' }}
                      >
                        <MenuItem value="Todos">Todos</MenuItem>
                        <MenuItem value="Cliente">Clientes</MenuItem>
                        <MenuItem value="Suplidor">Suplidores</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      Nombre
                    </Typography>
                    <TextField
                      fullWidth
                      size="medium"
                      onChange={(e) => registrarFiltroColumna('nombre', e.target.value)}
                      placeholder="Filtrar…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      RNC
                    </Typography>
                    <TextField
                      fullWidth
                      size="medium"
                      onChange={(e) => registrarFiltroColumna('rnc', e.target.value)}
                      placeholder="Filtrar…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      Ciudad
                    </Typography>
                    <TextField
                      fullWidth
                      size="medium"
                      onChange={(e) => registrarFiltroColumna('ciudad', e.target.value)}
                      placeholder="Filtrar…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      Teléfono
                    </Typography>
                    <TextField
                      fullWidth
                      size="medium"
                      value={filtrosColumna.telefono}
                      onChange={(e) => registrarFiltroColumna('telefono', e.target.value)}
                      placeholder="Filtrar…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={filtroColumnTituloSx}>
                      Email
                    </Typography>
                    <TextField
                      fullWidth
                      size="medium"
                      value={filtrosColumna.email}
                      onChange={(e) => registrarFiltroColumna('email', e.target.value)}
                      placeholder="Filtrar…"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      component="span"
                      sx={{
                        ...filtroColumnTituloSx,
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      Acciones
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.25 }}>
                      <Chip
                        title={`${clientesSuplidoresFiltrados.length} registros (filtrados)`}
                        label={`Total: ${clientesSuplidoresFiltrados.length}`}
                        sx={{
                          fontWeight: 700,
                          bgcolor: FP.color.accentBlueSoft,
                          color: FP.color.accentBlue,
                          border: 'none',
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </FpCard>

      <FpCard>
        <SectionHeader
          icon={<FormatListBulletedIcon sx={{ fontSize: 18 }} />}
          label="Registros"
        />
        <TableContainer
          sx={{
            maxHeight: { xs: 'min(55vh, 420px)', md: 520 },
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Table
            size="medium"
            sx={{
              ...registrosTableSx,
              '& td': { px: 2, py: 1.5, verticalAlign: 'top' },
            }}
          >
            {registrosColgroup}
            <TableBody>
              <TableRow
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  '& td': {
                    border: 'none',
                    borderBottom: `2px solid ${FP.color.border}`,
                    bgcolor: FP.color.surfaceAlt,
                  },
                }}
              >
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    Tipo
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    Nombre
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    RNC
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    Ciudad
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    Teléfono
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography component="span" sx={filtroColumnTituloSx}>
                    Email
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    component="span"
                    sx={{
                      ...filtroColumnTituloSx,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    Acciones
                  </Typography>
                </TableCell>
              </TableRow>
              {cargando ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: FP.color.textSecondary }}>Cargando...</Typography>
                  </TableCell>
                </TableRow>
              ) : datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
                      No se encontraron registros
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:hover': { bgcolor: FP.color.surface },
                      '& > td': { borderColor: FP.color.border },
                    }}
                  >
                    <TableCell>
                      <Chip
                        icon={item.tiposuplidor === 'Cliente' ? <PersonIcon /> : <BusinessIcon />}
                        label={item.tiposuplidor}
                        size="small"
                        sx={chipTipoSx(item.tiposuplidor === 'Cliente')}
                      />
                    </TableCell>
                    <TableCell sx={{ color: FP.color.textPrimary }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.descrip}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: FP.color.textPrimary }}>{item.rnc}</TableCell>
                    <TableCell sx={{ color: FP.color.textPrimary }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: FP.color.textMuted }} />
                        {item.ciudad}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: FP.color.textPrimary }}>
                      {item.telefono ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: FP.color.textMuted }} />
                          {item.telefono}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ color: FP.color.textPrimary }}>
                      {item.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 16, color: FP.color.textMuted }} />
                          {item.email}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Ver detalles">
                          <IconButton
                            size="small"
                            onClick={() => handleVerDetalles(item.id)}
                            sx={{ color: FP.color.info }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditar(item.id)}
                            sx={{ color: FP.color.navy }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleEliminar(item.id, item.tiposuplidor)}
                            sx={{ color: FP.color.danger }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPaginas > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: `1px solid ${FP.color.border}`,
            }}
          >
            <Typography variant="body2" sx={{ color: FP.color.textSecondary }}>
              Mostrando {inicio + 1} - {Math.min(fin, clientesSuplidoresFiltrados.length)} de{' '}
              {clientesSuplidoresFiltrados.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setPagina(Math.max(0, pagina - 1))}
                disabled={pagina === 0}
                sx={{ textTransform: 'none', fontWeight: 600, color: FP.color.navy }}
              >
                Anterior
              </Button>
              <Button
                size="small"
                onClick={() => setPagina(Math.min(totalPaginas - 1, pagina + 1))}
                disabled={pagina >= totalPaginas - 1}
                sx={{ textTransform: 'none', fontWeight: 600, color: FP.color.navy }}
              >
                Siguiente
              </Button>
            </Box>
          </Box>
        )}
      </FpCard>

      <Dialog
        open={dialogoVer}
        onClose={() => setDialogoVer(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
            Detalles del {clienteSeleccionado?.tiposuplidor || 'registro'}
          </Typography>
          <IconButton onClick={() => setDialogoVer(false)} size="small" sx={{ color: FP.color.textSecondary }}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {clienteSeleccionado && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  Nombre
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.descrip}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  RNC
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.rnc}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  Dirección
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.direccion || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  Ciudad
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.ciudad || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  Teléfono
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.telefono || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  WhatsApp/Celular
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.fax || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: FP.color.textSecondary }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: FP.color.textPrimary }}>
                  {clienteSeleccionado.email || '-'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
          <Button onClick={() => setDialogoVer(false)} sx={outlineBtnSx}>
            Cerrar
          </Button>
          {clienteSeleccionado && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setDialogoVer(false);
                handleEditar(clienteSeleccionado.id);
              }}
              sx={primaryBtnSx}
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (standalone) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Listado de Clientes y Suplidores" subtitle="Consulta y mantenimiento" />
        {contenido}
      </Box>
    );
  }

  return contenido;
};

export default ListadoClientesSuplidores;
